/**
 * AddressAutocomplete · v2.7.0 batch DF (2026-02)
 * ================================================
 * Custom dropdown over Google's AutocompleteService + Geocoder.
 * Replaces the legacy `google.maps.places.Autocomplete` widget which has
 * stopped reliably rendering its `.pac-container` inside React controlled
 * inputs + shadcn wrappers (verified silent failure across Chrome,
 * Safari, Firefox · Feb 2026).
 *
 * Why this rewrite:
 *   - Legacy widget was creating `.pac-container` but immediately hiding
 *     it (`display:none`) inside our React tree → riders saw no
 *     suggestions even though the Places API was returning 5 hits.
 *   - Google's own deprecation announcement (Mar 2025) makes the legacy
 *     widget unsafe to depend on going forward — `PlaceAutocompleteElement`
 *     is a Web Component that doesn't play nicely with React refs or
 *     our existing test ids.
 *   - Direct AutocompleteService gives us full UI control + dark theme
 *     + custom test ids + zero reliance on Google's internal DOM.
 *
 * Behavior preserved:
 *   - props.value, props.onChange(addr, placeObj|null), props.placeholder, props.testid
 *   - US-only, biased toward Oklahoma & surrounding states
 *   - Returns BOTH addresses and businesses (no `types` filter)
 *   - On selection: hydrates `formatted_address`, `geometry`, `name`,
 *     `place_id` via PlacesService.getDetails so downstream quote logic
 *     keeps working unchanged.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useConfig } from "@/contexts/ConfigContext";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

let scriptPromise = null;
function loadGoogleMaps(apiKey) {
  if (window.google?.maps?.places?.AutocompleteService) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    // NB: do NOT use `loading=async` here — that mode requires the new
    // `google.maps.importLibrary('places')` API to actually populate
    // window.google.maps.places. With the default loader the `onload`
    // event fires only after the `places` library is fully attached, so
    // our direct `new AutocompleteService()` works immediately.
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
    s.async = true; s.defer = true;
    s.onload = resolve;
    s.onerror = () => { scriptPromise = null; reject(new Error("Maps SDK failed to load")); };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

// Oklahoma + neighboring-states viewport bias (lat/lng box around OK)
const OK_BIAS = {
  north: 37.0, south: 33.6, east: -94.4, west: -103.0,
};

export default function AddressAutocomplete({ value, onChange, placeholder = "Enter Oklahoma address", testid }) {
  const { config } = useConfig();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const acServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  const [internal, setInternal] = useState(value || "");
  const [sdkReady, setSdkReady] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [fetching, setFetching] = useState(false);

  // Sync external value changes (e.g. parent setOrigin("") to reset the form)
  useEffect(() => { setInternal(value || ""); }, [value]);

  // Load the Maps JS SDK once on mount
  useEffect(() => {
    const apiKey = config?.google_maps_api_key || process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;
    loadGoogleMaps(apiKey)
      .then(() => {
        if (!window.google?.maps?.places) return;
        acServiceRef.current = new window.google.maps.places.AutocompleteService();
        // PlacesService needs an HTMLDivElement OR a Map; a hidden div works for
        // headless getDetails calls.
        const div = document.createElement("div");
        placesServiceRef.current = new window.google.maps.places.PlacesService(div);
        setSdkReady(true);
      })
      .catch(() => { /* swallow — fall back to plain free-text input */ });
  }, [config]);

  // Fresh session token = one billable unit per typing session per Places billing rules
  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current && window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  // Debounced predictions fetch
  const fetchPredictions = useCallback((query) => {
    if (!sdkReady || !acServiceRef.current) return;
    if (!query || query.trim().length < 2) {
      setSuggestions([]); setFetching(false); return;
    }
    setFetching(true);
    acServiceRef.current.getPlacePredictions({
      input: query.trim(),
      sessionToken: ensureSessionToken(),
      componentRestrictions: { country: "us" },
      // Legacy API uses `bounds` (LatLngBoundsLiteral) for viewport bias, NOT
      // `locationBias` (that's the Places API New). Mixing them up silently
      // 0-results the call.
      bounds: OK_BIAS,
    }, (preds, status) => {
      setFetching(false);
      // ZERO_RESULTS is also a "successful" response — surface as empty array.
      if ((status !== "OK" && status !== "ZERO_RESULTS") || !preds) {
        setSuggestions([]); return;
      }
      setSuggestions((preds || []).slice(0, 6));
      setHighlighted(-1);
    });
  }, [sdkReady, ensureSessionToken]);

  const onTextChange = (e) => {
    const v = e.target.value;
    setInternal(v);
    onChange?.(v, null);
    setOpenDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(v), 180);
  };

  const choose = useCallback((pred) => {
    if (!pred) return;
    // Best-effort hydrate geometry + canonical formatted_address via getDetails
    const finalize = (placeObj) => {
      const addr = placeObj?.formatted_address || pred.description;
      const nm = placeObj?.name || (pred.structured_formatting?.main_text || "");
      let full = addr;
      if (nm && addr && !addr.toLowerCase().startsWith(nm.toLowerCase())) full = `${nm}, ${addr}`;
      else if (nm && !addr) full = nm;
      setInternal(full);
      setSuggestions([]);
      setOpenDropdown(false);
      setHighlighted(-1);
      // Burn this session token — fresh one next typing session
      sessionTokenRef.current = null;
      onChange?.(full, placeObj || null);
    };
    if (!placesServiceRef.current || !pred.place_id) {
      finalize(null);
      return;
    }
    placesServiceRef.current.getDetails({
      placeId: pred.place_id,
      sessionToken: sessionTokenRef.current,
      fields: ["formatted_address", "geometry", "name", "place_id"],
    }, (place, status) => {
      finalize(status === "OK" ? place : null);
    });
  }, [onChange]);

  const onKey = (e) => {
    if (!openDropdown || suggestions.length === 0) {
      if (e.key === "ArrowDown" && suggestions.length === 0) setOpenDropdown(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (highlighted >= 0) {
        e.preventDefault();
        choose(suggestions[highlighted]);
      }
    } else if (e.key === "Escape") {
      setOpenDropdown(false);
    }
  };

  // Close dropdown on outside-click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpenDropdown(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} className="relative">
      <Input
        ref={inputRef}
        data-testid={testid}
        value={internal}
        onChange={onTextChange}
        onFocus={() => internal.trim().length >= 2 && setOpenDropdown(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        autoComplete="off"
        className="bg-zinc-900 border-zinc-700 text-zinc-100 placeholder:text-zinc-500 h-12"
      />
      {(!sdkReady || fetching) && (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-zinc-500" data-testid={`${testid}-loading`} />
      )}

      {openDropdown && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 mt-1 bg-zinc-950 border border-amber-400/40 rounded-md shadow-2xl shadow-amber-500/10 max-h-72 overflow-auto"
          data-testid={`${testid}-suggestions`}
          role="listbox"
        >
          {suggestions.map((s, idx) => {
            const main = s.structured_formatting?.main_text || s.description;
            const sub = s.structured_formatting?.secondary_text || "";
            const active = idx === highlighted;
            return (
              <li
                key={s.place_id || s.description}
                data-testid={`${testid}-suggestion-${idx}`}
                role="option"
                aria-selected={active}
                className={`px-3 py-2 cursor-pointer flex items-start gap-2.5 border-b border-zinc-900 last:border-b-0 ${
                  active ? "bg-amber-400/15" : "hover:bg-zinc-900"
                }`}
                onMouseEnter={() => setHighlighted(idx)}
                onMouseDown={(e) => { e.preventDefault(); choose(s); }}
              >
                <MapPin className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5"/>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-zinc-100 truncate">{main}</div>
                  {sub && <div className="text-[11px] text-zinc-500 truncate">{sub}</div>}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
